import { Ban, Building2, CalendarClock, CheckCircle2, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { personsApi } from '../../../human-resources/personnel/personnelApi';
import { RealPropertyModal } from '../components/RealPropertyModal';
import { OWNERSHIP, OWNERSHIP_LABEL } from '../inventoryTypes';
import { realPropertiesApi } from '../realPropertyApi';
import type { RealPropertyListItem } from '../realPropertyTypes';

type TabKey = 'info';

/**
 * One registered property.
 *
 * On the **header** layout: a property reads as one continuous thing rather than a record whose
 * content splits into panels a reader chooses between.
 *
 * **The custodian's name is FETCHED, never carried on the row.** The property holds a
 * `PersonnelId` and a `PersonId`; the name comes from the identity service in its own call, and
 * a property with no custodian simply does not make it. That is the same shape every other name
 * in this product takes, and it is why this page can show a name while the register's search
 * cannot reach one.
 */
export function RealPropertyProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [property, setProperty] = useState<RealPropertyListItem | null>(null);
  const [custodian, setCustodian] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      realPropertiesApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setProperty(next);
          setError(null);

          /*
            The custodian's NAME, in its own call and only where there is one to fetch. It is a
            second request with a different lifetime rather than a field on the record — the
            same split the photograph and the identity reveal already take.

            A failure here leaves the name unresolved and the page standing: not knowing who
            answers for a building is a smaller loss than a page that will not render.
          */
          if (next.accountablePersonId === null) {
            setCustodian(null);
            return;
          }

          personsApi
            .resolve([next.accountablePersonId], signal)
            .then((people) => {
              if (signal?.aborted) return;
              setCustodian(people[0]?.displayName ?? null);
            })
            .catch(() => setCustodian(null));
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError ? caught : new ApiError('Could not load this property.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /**
   * Puts a deleted property back.
   *
   * **No confirm tick and no password** — a restore puts a record back. It CAN fail, though:
   * the code index is filtered, so deleting this property freed its code and another may have
   * taken it since. The server re-checks and the refusal lands in the banner.
   */
  async function restore() {
    if (!id) return;

    setRestoring(true);
    setError(null);

    try {
      await realPropertiesApi.activate(id);
      load();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not restore this property.', 0),
      );
    } finally {
      setRestoring(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Logistics',
    'Inventory',
    { label: 'Settings', to: '/inventory/settings' },
    { label: 'Real Properties', to: '/inventory/settings/real-properties' },
    property?.name ?? 'Property',
  ];

  if (error && !property) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={() => load()}
        />
      </div>
    );
  }

  if (loading || !property) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const leased = property.ownership === OWNERSHIP.leased;

  /**
   * The banner, and every state renders — one that appears only when something is wrong teaches
   * the reader to skip the region.
   */
  function state() {
    if (error) {
      return {
        tone: 'red' as const,
        icon: <Ban className="h-4 w-4" aria-hidden="true" />,
        message: error.message,
      };
    }

    if (!property!.isActive) {
      return {
        tone: 'red' as const,
        icon: <Ban className="h-4 w-4" aria-hidden="true" />,
        message:
          `This property is deleted. Its code ${property!.propertyCode} is FREE for another ` +
          'property to take, so a restore is re-checked and can fail.',
      };
    }

    if (leased && property!.isLeaseExpired) {
      return {
        tone: 'red' as const,
        icon: <CalendarClock className="h-4 w-4" aria-hidden="true" />,
        message: property!.leaseEndDate
          ? `The lease ended on ${formatDateOnly(property!.leaseEndDate)} and has not been renewed here.`
          : 'The lease has lapsed.',
      };
    }

    if (leased && property!.leaseEndDate !== null) {
      return {
        tone: 'blue' as const,
        icon: <CalendarClock className="h-4 w-4" aria-hidden="true" />,
        message: `Leased, with the term running to ${formatDateOnly(property!.leaseEndDate)}.`,
      };
    }

    if (leased) {
      return {
        tone: 'amber' as const,
        icon: <CalendarClock className="h-4 w-4" aria-hidden="true" />,
        message:
          'Leased, with no end date recorded — so it cannot appear on a renewal list. Add the ' +
          'term to have it surface before it runs out.',
      };
    }

    return {
      tone: 'slate' as const,
      icon: <CheckCircle2 className="h-4 w-4" aria-hidden="true" />,
      message: `Held outright as ${property!.typeName}.`,
    };
  }

  const banner = state();

  // ONE Edit, in the cog, on every card — a card is a section and a section is one form. The
  // form is section-less here because a property is one short record rather than a wizard.
  const edit = {
    key: 'edit-property',
    label: 'Edit',
    permission: 'real-properties.update',
    onClick: () => setEditing(true),
  };

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="real-property-profile"
        header={
          <ProfileHeader
            id="real-property-profile-header"
            icon={<Building2 className="h-6 w-6" aria-hidden="true" />}
            title={property.name}
            meta={[property.propertyCode, property.typeName, property.inventoryCategoryName]}
            secondary={[
              OWNERSHIP_LABEL[property.ownership],
              property.locationLabel ?? 'No address recorded',
            ]}
            status={
              <StatusText tone={property.isActive ? 'active' : 'inactive'}>
                {property.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={property.createdByName}
            createdAt={property.createdAt}
          />
        }
        banner={
          <ProfileBanner
            id="real-property-profile-banner"
            tone={banner.tone}
            icon={banner.icon}
            message={banner.message}
            action={
              !property.isActive && !error ? (
                // Gates the button, not the banner: a read-only viewer still needs the state.
                <PermissionGate permission="real-properties.delete">
                  <Button
                    id="real-property-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void restore()}
                    disabled={restoring}
                    loading={restoring}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              ) : undefined
            }
          />
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard id="real-property-identity" title="Property" menuItems={[edit]}>
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Code', value: property.propertyCode, mono: true },
              { label: 'Name', value: property.name },
              { label: 'Type', value: property.typeName },
              { label: 'Category', value: property.inventoryCategoryName },
              { label: 'Tenure', value: OWNERSHIP_LABEL[property.ownership] },
              { label: 'Notes', value: property.notes },
            ]}
          />
        </ProfileCard>

        <ProfileCard id="real-property-location" title="Location" menuItems={[edit]}>
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Address', value: property.locationLabel },
              { label: 'Lot No / Street / Bldg', value: property.streetAddress },
              { label: 'Barangay', value: property.barangay },
              { label: 'City / Municipality', value: property.cityMunicipality },
              { label: 'Province', value: property.province },
              {
                label: 'Floor area',
                value: property.floorAreaSqm === null ? null : `${property.floorAreaSqm} sqm`,
                mono: true,
              },
              {
                label: 'Floors',
                value: property.floorCount === null ? null : String(property.floorCount),
                mono: true,
              },
              {
                label: 'Units',
                value: property.unitCount === null ? null : String(property.unitCount),
                mono: true,
              },
            ]}
          />
        </ProfileCard>

        {/*
          ONE card or the other, never both — the tenure decides which applies, and the server
          clears the half it does not. A card of em dashes would state that the property has no
          title when in fact it cannot have one.
        */}
        {leased ? (
          <ProfileCard id="real-property-lease" title="Lease" menuItems={[edit]}>
            <PreviewTable
              variant="table-display"
              rows={[
                {
                  label: 'Rent per cycle',
                  value: property.monthlyRent === null ? null : formatMoney(property.monthlyRent),
                  mono: true,
                },
                {
                  label: 'Lease starts',
                  // CALENDAR dates throughout, never the instant path — a lease runs on DAYS.
                  value:
                    property.leaseStartDate === null
                      ? null
                      : formatDateOnly(property.leaseStartDate),
                  mono: true,
                },
                {
                  label: 'Lease ends',
                  value:
                    property.leaseEndDate === null ? null : formatDateOnly(property.leaseEndDate),
                  mono: true,
                },
                {
                  label: 'Term',
                  value: property.isLeaseExpired ? (
                    <StatusText tone="blocked">lapsed</StatusText>
                  ) : property.leaseEndDate === null ? null : (
                    <StatusText tone="active">running</StatusText>
                  ),
                },
                {
                  // Said out loud rather than left as a gap the reader hunts for.
                  label: 'Lessor',
                  value: 'Not recorded — a lessor is a supplier, and that module is not built.',
                },
              ]}
            />
          </ProfileCard>
        ) : (
          <ProfileCard id="real-property-title" title="Title and acquisition" menuItems={[edit]}>
            <PreviewTable
              variant="table-display"
              rows={[
                { label: 'Title number', value: property.titleNumber, mono: true },
                { label: 'Lot number', value: property.lotNumber, mono: true },
                {
                  label: 'Acquisition cost',
                  value:
                    property.acquisitionCost === null
                      ? null
                      : formatMoney(property.acquisitionCost),
                  mono: true,
                },
                {
                  label: 'Acquired',
                  value:
                    property.acquisitionDate === null
                      ? null
                      : formatDateOnly(property.acquisitionDate),
                  mono: true,
                },
              ]}
            />
          </ProfileCard>
        )}

        <ProfileCard
          id="real-property-custody"
          title="Accountability"
          menuItems={[edit]}
          empty={
            property.accountablePersonnelId === null
              ? {
                  title: 'Nobody is accountable for this property',
                  description:
                    'Custody is standing — one employee answers for it until somebody reassigns them.',
                }
              : undefined
          }
          editPermission="real-properties.update"
          onEdit={() => setEditing(true)}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              {
                label: 'Accountable employee',
                /*
                  The resolved name, or a plain sentence where the resolve did not come back —
                  never an em dash, which would state that nobody is accountable when in fact
                  somebody is and this page could not name them.
                */
                value: custodian ?? 'Name unavailable',
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <RealPropertyModal
          open
          editing={property}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
